define(function (require, exports, module) {
    'use strict';

    var features = require('Cloud/Application/Features');
    var convertArrayBufferToHexadecimalString = require('../convertArrayBufferToHexadecimalString');
    var UploadReason = require('../../UploadReason');
    var JavaScriptError = require('../../errors/JavaScriptError');
    var LocalFileNotFoundFail = require('../../fails/LocalFileNotFoundFail');
    var UnsupportedFolderTransferFail = require('../../fails/UnsupportedFolderTransferFail');
    var LocalFileNotReadableFail = require('../../fails/LocalFileNotReadableFail');

    var MB = 1024 * 1024;

    /**
     * @namespace
     */
    var fileSystemFileReader = {
        /**
         * На самом деле значение гораздо больше,
         * но нас интересуют файлы меньше 21 байт.
         * @type {number}
         */
        MAX_BLOB_SIZE_PER_READ: 21,

        /**
         * В Safari возникает ошибка при попытке прочитать файл более 2GB.
         * Firefox выбрасывает исключение NS_ERROR_OUT_OF_MEMORY,
         * когда размер файла превышает объем свободной памяти.
         * В Chrome может падать вкладка, когда не хватает памяти.
         * Поэтому будем пытаться читать файлы только небольшого размера.
         * @type {number}
         */
        MAX_READABLE_SIZE: features.getFeatureParam('newUpload', 'maxReadableSize', 30) * MB,

        /**
         * @returns {Promise}
         * @resolves {FileReader}
         * @rejects {Error}
         */
        createReader: function () {
            var promise = new Promise(function (resolve) {
                var reader = new FileReader();

                resolve(reader);
            });

            return promise;
        },

        /**
         * @param {FileReader} reader
         * @param {File} file
         * @param startIdx? - для чтения не всего файла, а блока байт из него
         * @param stopIdx?
         * @param whole? - читать до первого прогресс евента
         * @returns {Promise}
         * @resolves {ArrayBuffer}
         * @rejects {UploadReason|Error}
         */
        _readFile: function (reader, file, startIdx = null, stopIdx = null, whole = false) {
            let promise = new Promise(function (resolve, reject) {
                const handler = (event) => {
                    let promise;

                    if (event.type === 'error') {
                        var source = UploadReason.SOURCE_WEB_CLIENT;
                        var stack;
                        var reason;
                        var error = reader.error;

                        if (LocalFileNotFoundFail.isNotFoundError(error)) {
                            stack = new Error('LocalFileNotFoundFail');
                            reason = new LocalFileNotFoundFail(stack, source);
                        } else if (!file.type && UnsupportedFolderTransferFail.isNotReadableError(error)) {
                            stack = new Error('UnsupportedFolderTransferFail');
                            reason = new UnsupportedFolderTransferFail(stack, source);
                        } else if (LocalFileNotReadableFail.isNotReadableError(error)) {
                            stack = new Error('LocalFileNotReadableFail');
                            reason = new LocalFileNotReadableFail(stack, source);
                        } else {
                            stack = new Error('JavaScriptError');
                            reason = new JavaScriptError(stack, source, error);
                        }

                        promise = Promise.reject(reason);
                    } else {
                        if (reader.readyState === reader.LOADING) {
                            reader.abort();
                        }

                        if (reader.readyState === FileReader.DONE) {
                            promise = Promise.resolve(reader.result);
                        }

                        if (!whole) {
                            // resolve first progress
                            promise = Promise.resolve(reader.result);
                        }
                    }

                    reader.removeEventListener('error', handler);
                    reader.removeEventListener('load', handler);
                    reader.removeEventListener('loadend', handler);
                    if (!whole) {
                        reader.removeEventListener('progress', handler);
                    }

                    resolve(promise);
                };

                reader.addEventListener('error', handler);
                reader.addEventListener('load', handler);
                reader.addEventListener('loadend', handler);
                if (!whole) {
                    reader.addEventListener('progress', handler);
                }
                const blob = startIdx >= 0 && stopIdx !== null ? file.slice(startIdx, stopIdx + 1) : file;
                reader.readAsArrayBuffer(blob);
            });

            if (!whole) {
                // Если файл не считывается целиком, значит чтение используется
                // для проверки существования файла, поэтому чтобы не блокировать
                // возможность аплоада, игнорируем ошибку "Out of Memory".
                promise = promise.catch(function (reason) {
                    var promise;

                    if (reason && reason.name === 'NS_ERROR_OUT_OF_MEMORY') {
                        promise = Promise.resolve(null);
                    } else {
                        promise = Promise.reject(reason);
                    }

                    return promise;
                });
            }

            return promise;
        },

        /**
         * @param {File} file
         * @returns {Promise}
         * @resolves {File}
         * @rejects {UploadReason|Error}
         */
        readFile: function (file) {
            var promise;

            if (file.size > fileSystemFileReader.MAX_READABLE_SIZE) {
                promise = Promise.reject(new Error('too large'));
            } else {
                promise = fileSystemFileReader.createReader(file);

                promise = promise.then(function (reader) {
                    var promise = fileSystemFileReader._readFile(reader, file);

                    return promise;
                });

                promise = promise.then(function () {
                    return file;
                });
            }

            return promise;
        },

        /**
         * @param {File} file
         * @param startIdx? - для чтения не всего файла, а блока байт из него
         * @param stopIdx
         * @returns {Promise}
         * @resolves {File content as ArrayBuffer}
         * @rejects {UploadReason|Error}
         */
        readFileContent: function (file, startIdx, stopIdx) {
            let promise = fileSystemFileReader.createReader();

            promise = promise.then(function (reader) {
                return fileSystemFileReader._readFile(reader, file, startIdx, stopIdx, true);
            });

            return promise;
        },

        /**
         * @param {File} file
         * @returns {Promise}
         * @resolves {FormData}
         * @rejects {Error}
         */
        getMultipartFormData: function (file) {
            var promise = new Promise(function (resolve) {
                var formData = new FormData();

                formData.append('file', file);
                resolve(formData);
            });

            return promise;
        },

        /**
         * @param {File} file
         * @returns {Promise}
         * @resolves {string}
         * @rejects {UploadReason|Error}
         */
        getHexCode: function (file) {
            var promise;

            if (file.size > fileSystemFileReader.MAX_BLOB_SIZE_PER_READ) {
                promise = Promise.reject(new Error('too large'));
            } else {
                promise = fileSystemFileReader.createReader(file);

                promise = promise.then(function (reader) {
                    var whole = true;
                    var promise = fileSystemFileReader._readFile(reader, file, null, null, whole);

                    return promise;
                });

                promise = promise.then(function (arrayBuffer) {
                    var hexCode = convertArrayBufferToHexadecimalString(arrayBuffer);

                    return hexCode;
                });
            }

            return promise;
        },
    };

    module.exports = fileSystemFileReader;
});
